import { routes } from 'constants/routing';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import { useAccountAddress } from 'libs/wallet';

import liquidityHubIconSrc from 'assets/img/liquidityHubIcon.svg';
import vaultsIconSrc from 'assets/img/vaultsIcon.svg';
import venusCoreIconSrc from 'assets/img/venusCoreIcon.png';
import venusFluxIconSrc from 'assets/img/venusFluxIcon.png';
import { VENUS_FLUX_URL } from 'constants/production';
import { useGetMarketsPagePath } from 'hooks/useGetMarketsPagePath';
import { useTranslation } from 'libs/translations';
import type { MenuItem, SubMenu } from '../types';

export const useMenuItems = () => {
  const { t } = useTranslation();
  const { accountAddress } = useAccountAddress();
  const swapRouteEnabled = useIsFeatureEnabled({ name: 'swapRoute' });
  const vaiRouteEnabled = useIsFeatureEnabled({ name: 'vaiRoute' });
  const bridgeRouteEnabled = useIsFeatureEnabled({ name: 'bridgeRoute' });
  const statsRouteEnabled = useIsFeatureEnabled({ name: 'statsRoute' });
  const tradeRouteEnabled = useIsFeatureEnabled({ name: 'trade' });
  const primeLeaderboardEnabled = useIsFeatureEnabled({ name: 'primeLeaderboard' });
  const liquidityHubEnabled = useIsFeatureEnabled({ name: 'liquidityHub' });
  const { marketsPagePath } = useGetMarketsPagePath();

  const menu: Array<MenuItem | SubMenu> = [];

  menu.push({
    to: routes.dashboard.path,
    label: t('layout.menu.dashboard.label'),
  });

  const marketsSubMenu: SubMenu = {
    label: t('layout.menu.markets.label'),
    variant: 'secondary',
    items: [
      {
        to: marketsPagePath,
        imgSrc: venusCoreIconSrc,
        label: t('layouts.menu.markets.venusCore.label'),
        description: t('layouts.menu.markets.venusCore.description'),
      },
      {
        href: VENUS_FLUX_URL,
        imgSrc: venusFluxIconSrc,
        label: t('layouts.menu.markets.venusFlux.label'),
        description: t('layouts.menu.markets.venusFlux.description'),
      },
    ],
  };

  if (liquidityHubEnabled) {
    menu.push(
      {
        label: t('layout.menu.earn.label'),
        variant: 'secondary',
        tagLabel: t('layout.menu.new'),
        items: [
          {
            to: routes.liquidityHubs.path,
            imgSrc: liquidityHubIconSrc,
            label: t('layouts.menu.markets.liquidityHub.label'),
            tagLabel: t('layout.menu.new'),
            description: t('layouts.menu.markets.liquidityHub.description'),
          },
          {
            to: routes.vaults.path,
            imgSrc: vaultsIconSrc,
            label: t('layouts.menu.markets.vaults.label'),
            description: t('layouts.menu.markets.vaults.description'),
          },
        ],
      },
      marketsSubMenu,
    );
  } else {
    menu.push(marketsSubMenu, {
      to: routes.vaults.path,
      label: t('layout.menu.vaults.label'),
    });
  }

  const othersSubMenuItems: MenuItem[] = [
    {
      to: routes.governance.path,
      iconName: 'market',
      label: t('layout.menu.others.governance.label'),
      description: t('layout.menu.others.governance.description'),
    },
  ];

  if (vaiRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.vai.path,
      iconName: 'vaiOutline',
      label: t('layout.menu.others.vai.label'),
      description: t('layout.menu.others.vai.description'),
    });
  }

  if (bridgeRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.bridge.path,
      iconName: 'bridge',
      label: t('layout.menu.others.bridge.label'),
      description: t('layout.menu.others.bridge.description'),
    });
  }

  if (swapRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.swap.path,
      iconName: 'convert',
      label: t('layout.menu.others.swap.label'),
    });
  }

  if (accountAddress) {
    othersSubMenuItems.push({
      to: routes.port.path,
      iconName: 'download',
      label: t('layout.menu.others.port.label'),
      description: t('layout.menu.others.port.description'),
    });
  }

  if (primeLeaderboardEnabled) {
    menu.push({
      to: routes.primeLeaderboard.path,
      label: t('layout.menu.prime.label'),
    });
  }

  if (statsRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.stats.path,
      iconName: 'stats',
      label: t('layout.menu.others.stats.label'),
      description: t('layout.menu.others.stats.description'),
    });
  }

  if (tradeRouteEnabled) {
    othersSubMenuItems.push({
      to: routes.trade.path,
      iconName: 'trade',
      label: t('layout.menu.others.trade.label'),
      description: t('layout.menu.others.trade.description'),
      tagLabel: t('layout.menu.beta'),
    });
  }

  menu.push({
    label: t('layout.menu.others.label'),
    items: othersSubMenuItems,
  });

  return menu;
};
